import { Card, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import moment from "moment"
import { FaBus, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaTachometerAlt, FaFlagCheckered, FaIdCard, FaCogs, FaExternalLinkAlt } from "react-icons/fa"
import { routeForecastUrl } from "../../utils/routeForecastLink"
import { translations } from "../../utils/constants"
import { formatOperatingDays } from "../GtfsUpload/GtfsUpload"

// GTFS colors arrive as bare hex; white/empty means "no brand color", so the
// badge falls back to the app theme red.
const routeBadgeColor = (c) =>
    c && c.toUpperCase() !== "FFFFFF" ? `#${c.replace("#", "")}` : "#ED2328"
const routeBadgeTextColor = (c, tc) =>
    c && c.toUpperCase() !== "FFFFFF" && tc ? `#${tc.replace("#", "")}` : "#fff"

// forecastDevice: device ID (ud_sn / serial) to deep-link the public
// /route-forecast page with; omitted on that page itself.
const VehicleRoute = ({ vehicle, past = [], current = null, future = [], loading = false, language, vehicleStops, schedule = null, forecastDevice = null }) => {
    const forecastHref = routeForecastUrl(forecastDevice)
    const renderForecastLink = (className) => forecastHref ? (
        <a
            className={className}
            href={forecastHref}
            target="_blank"
            rel="noopener noreferrer"
            title={translations[language]?.openRouteForecast}
        >
            <FaExternalLinkAlt /> {translations[language]?.routeForecastLink}
        </a>
    ) : null

    // The full stop list renders (all past stops included, scrollable); on
    // first load of a trip the list jumps to the current stop so the user
    // lands on "now". On later refreshes the current stop advances down the
    // list as the run progresses — whenever it has drifted out of view the
    // list re-centers on it, so the "now" row is always on screen. When it
    // is already visible the scroll position is left alone.
    const currentRowRef = useRef(null)
    const listRef = useRef(null)
    const lastScrollKeyRef = useRef(null)
    // Wall-clock of the last schedule payload — shown in the header so the
    // operator can see the panel is still being refreshed.
    const [refreshedAt, setRefreshedAt] = useState(null)
    useEffect(() => {
        if (schedule) setRefreshedAt(new Date())
    }, [schedule])
    const currentSequence = (schedule?.stops || []).find((s) => s.status === "current")?.sequence
    const scrollKey = schedule?.trip_id
        ? `${schedule.vehicle_id}-${schedule.trip_id}`
        : (currentSequence != null ? `seq-${currentSequence}` : null)

    // Scrolls the list (not the page) so the current stop sits mid-panel.
    // Returns false when the list has no layout yet — the drawer is still
    // mounting/sliding in — so the caller can retry.
    const centerOnCurrent = (behavior = "auto") => {
        const row = currentRowRef.current
        const list = listRef.current
        if (!row || !list) return false
        if (!list.clientHeight || list.scrollHeight <= list.clientHeight) return false
        const rowRect = row.getBoundingClientRect()
        const listRect = list.getBoundingClientRect()
        const target = list.scrollTop + (rowRect.top - listRect.top) - (list.clientHeight / 2 - rowRect.height / 2)
        const clamped = Math.max(0, Math.min(target, list.scrollHeight - list.clientHeight))
        if (typeof list.scrollTo === "function") list.scrollTo({ top: clamped, behavior })
        else list.scrollTop = clamped
        return true
    }

    // First time a trip's stop list is shown (drawer just opened, or the bus
    // moved onto another run) jump to the current stop. The drawer content
    // is measured before its slide-in animation finishes, so keep retrying
    // on animation frames until the list is actually scrollable.
    useEffect(() => {
        if (loading || !scrollKey) return
        if (lastScrollKeyRef.current === scrollKey) return

        let cancelled = false
        let attempts = 0
        const tryCenter = () => {
            if (cancelled) return
            if (centerOnCurrent("auto")) {
                lastScrollKeyRef.current = scrollKey
                return
            }
            attempts += 1
            if (attempts < 90) requestAnimationFrame(tryCenter)
        }
        tryCenter()
        return () => { cancelled = true }
    }, [scrollKey, loading, schedule?.stops?.length])

    // Later refreshes: the current stop advances down the list as the run
    // progresses — whenever it has drifted out of view the list re-centers on
    // it. When it is already visible the scroll position is left alone.
    useEffect(() => {
        if (loading || !scrollKey || lastScrollKeyRef.current !== scrollKey) return
        const row = currentRowRef.current
        const list = listRef.current
        if (!row || !list) return
        const rowRect = row.getBoundingClientRect()
        const listRect = list.getBoundingClientRect()
        const outOfView = rowRect.top < listRect.top || rowRect.bottom > listRect.bottom
        if (outOfView) centerOnCurrent("smooth")
    }, [currentSequence, loading, schedule?.stops?.length])

    // The list gets its real height only once the drawer has finished
    // opening (and again on window resizes): re-center then if the initial
    // attempt happened while it was still collapsed.
    useEffect(() => {
        const list = listRef.current
        if (!list || typeof ResizeObserver === "undefined") return
        const observer = new ResizeObserver(() => {
            if (loading || !scrollKey) return
            if (lastScrollKeyRef.current !== scrollKey && centerOnCurrent("auto")) {
                lastScrollKeyRef.current = scrollKey
            }
        })
        observer.observe(list)
        return () => observer.disconnect()
    }, [scrollKey, loading])

    // moment, not new Date(): the backend sends 'YYYY-MM-DD HH:mm:ss', which
    // Safari's Date parser rejects.
    const formatTime = (t) => {
        if (!t) return "--:--"
        const m = moment(t, "YYYY-MM-DD HH:mm:ss")
        return m.isValid() ? m.format("h:mm A") : "--:--"
    }

    // Clock-only render of the position's report time — matches the router
    // platform's "Report time" so coordinates can be compared 1:1.
    const formatReportTime = (t) => {
        if (!t) return null
        const m = /^\d{10}$/.test(String(t))
            ? moment.unix(Number(t))
            : moment(t, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true)
        return m.isValid() ? m.format("HH:mm:ss") : String(t)
    }

    if (loading) {
        return (
            <div className="vr-loading">
                <div className="vr-skeleton-card" />
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div className="vr-skeleton-stop" key={i}>
                        <span className="vr-skeleton-time" />
                        <span className="vr-skeleton-dot" />
                        <span className="vr-skeleton-row" style={{ width: `${62 - (i % 3) * 12}%` }} />
                    </div>
                ))}
            </div>
        )
    }

    // Why the schedule didn't match, in words the operator can act on.
    const SCHEDULE_REASONS = {
        en: {
            no_gtfs_feed_attached: "No GTFS schedule attached to this vehicle",
            no_router_device: "No router device linked to this vehicle",
            no_gps_today: "No GPS data received today yet",
            no_service_today: "No scheduled service today",
            bus_not_near_any_stop: "Bus is not near any scheduled stop",
            no_scheduled_passages_nearby: "No scheduled passages near the bus",
            no_trip_matches_current_time: "No scheduled run matches the current time",
            no_credible_trip_match: "Live GPS doesn't reliably match any scheduled run right now",
            awaiting_departure: "Vehicle is at its starting point — the route appears when the run begins",
            trip_completed: "Vehicle has reached the last stop of its run — the route appears again when the next run begins",
        },
        it: {
            no_gtfs_feed_attached: "Nessun orario GTFS collegato a questo veicolo",
            no_router_device: "Nessun router collegato a questo veicolo",
            no_gps_today: "Nessun dato GPS ricevuto oggi",
            no_service_today: "Nessun servizio programmato oggi",
            bus_not_near_any_stop: "Il bus non è vicino a nessuna fermata",
            no_scheduled_passages_nearby: "Nessun passaggio programmato vicino al bus",
            no_trip_matches_current_time: "Nessuna corsa programmata corrisponde all'orario attuale",
            no_credible_trip_match: "Il GPS non corrisponde in modo affidabile a nessuna corsa al momento",
            awaiting_departure: "Il veicolo è al capolinea — il percorso appare all'inizio della corsa",
            trip_completed: "Il veicolo ha raggiunto l'ultima fermata della corsa — il percorso riappare all'inizio della corsa successiva",
        },
    }

    // Today's runs (from the schedule response's `today` summary): a
    // "run completed" banner when the bus has just reached its last stop,
    // and the day's tally with the last completion time in both views.
    const RUN_SUMMARY = {
        en: {
            runCompleted: "Run completed",
            runCompletedAt: "last stop reached at",
            runsToday: "Runs completed today",
            ofMatched: "of",
            lastRun: "Last run completed",
            noRunsYet: "No run completed yet today",
            line: "line",
        },
        it: {
            runCompleted: "Corsa completata",
            runCompletedAt: "ultima fermata raggiunta alle",
            runsToday: "Corse completate oggi",
            ofMatched: "su",
            lastRun: "Ultima corsa completata",
            noRunsYet: "Nessuna corsa completata oggi finora",
            line: "linea",
        },
    }
    const rs = RUN_SUMMARY[language] || RUN_SUMMARY.en
    const today = schedule?.today || null
    const fmtTime = (iso) => (iso && moment(iso).isValid() ? moment(iso).format("HH:mm") : null)
    const renderRunsSummary = () => {
        if (!today) return null
        const lastAt = fmtTime(today.last_completed_at)
        return (
            <div className="vrs-runs">
                <div className="vrs-runs-row">
                    <FaFlagCheckered className="vrs-runs-icon" />
                    <span>{rs.runsToday}:</span>
                    <strong>{today.runs_completed}</strong>
                    {today.runs_matched > today.runs_completed && (
                        <span className="vrs-runs-muted">{rs.ofMatched} {today.runs_matched}</span>
                    )}
                </div>
                <div className="vrs-runs-row">
                    <FaClock className="vrs-runs-icon" />
                    {lastAt ? (
                        <span>
                            {rs.lastRun}: <strong>{lastAt}</strong>
                            {today.last_completed_line ? ` · ${rs.line} ${today.last_completed_line}` : ""}
                            {today.last_completed_headsign ? ` → ${today.last_completed_headsign}` : ""}
                        </span>
                    ) : (
                        <span className="vrs-runs-muted">{rs.noRunsYet}</span>
                    )}
                </div>
            </div>
        )
    }
    const renderCompletedBanner = () => {
        if (schedule?.reason !== "trip_completed") return null
        const lastAt = fmtTime(today?.last_completed_at)
        return (
            <div className="vrs-done-note">
                <FaFlagCheckered />
                <span>
                    <strong>{rs.runCompleted}</strong>
                    {lastAt ? ` — ${rs.runCompletedAt} ${lastAt}` : ""}
                </span>
            </div>
        )
    }

    // ------- Schedule mode: the client's official GTFS timetable drives the
    // panel; the only live element is the early/late offset. -------
    if (schedule?.schedule_available) {
        const t = translations[language] || {}
        const stops = schedule.stops || []
        const delay = schedule.delay_minutes || 0
        const status = schedule.delay_status
        // Provisional = assigned line's timetable without live GPS evidence:
        // scheduled times only, no delay claims.
        const isProvisional = !!schedule.provisional
        // Live view rows always show the delay-adjusted forecast time (the
        // chip states the delay separately) — even a 1-2 min "on time"
        // offset shifts the times, matching how official displays forecast.
        const useEstimated = !isProvisional

        const pastStops = stops.filter((s) => s.status === "past")
        // ALL past stops stay in the list — the panel auto-scrolls to the
        // current stop, so history is one scroll-up away instead of cut off.
        const shownPast = pastStops
        const currentStop = stops.find((s) => s.status === "current")
        const futureStops = stops.filter((s) => s.status === "future")
        // Run progress for the header bar: served stops over the whole run.
        const doneCount = pastStops.length + (currentStop ? 1 : 0)
        const progressPct = stops.length ? Math.round((doneCount / stops.length) * 100) : 0
        const lastStop = stops[stops.length - 1]

        const chipClass = isProvisional
            ? "vrs-chip vrs-chip-provisional"
            : status === "late" ? "vrs-chip vrs-chip-late"
                : status === "early" ? "vrs-chip vrs-chip-early"
                    : "vrs-chip vrs-chip-ontime"
        const chipText = isProvisional
            ? t.awaitingGpsChip
            : status === "late" ? `${t.delayChip} ${Math.abs(delay)} min`
                : status === "early" ? `${t.earlyChip} ${Math.abs(delay)} min`
                    : t.onTimeChip

        const badgeBg = routeBadgeColor(schedule.route?.color)
        const badgeFg = routeBadgeTextColor(schedule.route?.color, schedule.route?.text_color)

        const StopRow = ({ s, kind, isLastOfBlock, rowRef }) => (
            <div ref={rowRef} className={`vrs-row vrs-row-${kind}`} key={`${s.gtfs_stop_id}-${s.sequence}`}>
                <div className="vrs-times">
                    <span className="vrs-time-single">
                        {useEstimated && s.estimated ? s.estimated : s.scheduled}
                    </span>
                </div>
                <div className="vrs-timeline">
                    <span className={`vrs-dot vrs-dot-${kind}`} />
                    {!isLastOfBlock && <span className={`vrs-line vrs-line-${kind}`} />}
                </div>
                <div className="vrs-stop-name">{s.name}</div>
                {kind !== "past" && Number.isFinite(s.eta_minutes) && (
                    <div className="vrs-eta">
                        {s.eta_minutes <= 0 ? (
                            <>⏱ <strong>{t.arrivingNow}</strong></>
                        ) : (
                            <>⏱ {t.arriveInShort} <strong>{s.eta_minutes}</strong> min</>
                        )}
                    </div>
                )}
            </div>
        )

        const provisionalReason =
            isProvisional && schedule.reason
                ? (SCHEDULE_REASONS[language] || SCHEDULE_REASONS.en)[schedule.reason] || null
                : null

        // Public forecast page: the name is what the operator typed for the
        // vehicle; fall back to plate / registering number / model so the chip
        // never renders as a bare "(E1812)" when the name was left empty.
        const vehiclePlate = vehicle?.licensePlate || vehicle?.registeringNumber || null
        const vehicleLabel = vehicle?.vehicleName || vehiclePlate || vehicle?.vehicleModel || t.vehicle || "Vehicle"

        return (
            <div className="vr-container vrs-container">
                {renderRunsSummary()}
                {provisionalReason && (
                    <div className="vrs-fallback-note">ⓘ {provisionalReason}</div>
                )}
                <div className="vrs-header">
                    <div className="vrs-header-top">
                        <span className="vrs-line-badge" style={{ backgroundColor: badgeBg, color: badgeFg }}>
                            {schedule.route?.short_name || "•"}
                        </span>
                        <div className="vrs-line-names">
                            <div className="vrs-line-long">{schedule.route?.long_name || t.lineLabel}</div>
                            {schedule.route?.headsign && (
                                <div className="vrs-headsign">→ {schedule.route.headsign}</div>
                            )}
                        </div>
                        <span className={chipClass}>{chipText}</span>
                    </div>
                    <div className="vrs-meta">
                        <span className="vrs-meta-chip vrs-meta-vehicle">
                            <FaBus />
                            {vehicleLabel}
                            {vehicle?.ud_sn ? ` (${vehicle.ud_sn})` : ''}
                        </span>
                        {vehiclePlate && vehiclePlate !== vehicleLabel && (
                            <span className="vrs-meta-chip"><FaIdCard /> {vehiclePlate}</span>
                        )}
                        {vehicle?.vehicleModel && vehicle.vehicleModel !== vehicleLabel && (
                            <span className="vrs-meta-chip"><FaCogs /> {vehicle.vehicleModel}</span>
                        )}
                        {renderForecastLink("vrs-meta-chip vrs-meta-forecast")}
                        {Number.isFinite(schedule.speed_kmh) && (
                            <span className="vrs-meta-chip vrs-meta-speed"><FaTachometerAlt /> {schedule.speed_kmh} km/h</span>
                        )}
                        {schedule.route?.days?.length ? (
                            <span className="vrs-meta-chip"><FaCalendarAlt /> {formatOperatingDays(schedule.route.days, language)}</span>
                        ) : null}
                        {isProvisional && !currentStop && pastStops.length === 0 && futureStops.length > 0 && (
                            <span className="vrs-meta-chip"><FaClock /> {t.nextRunAt} {futureStops[0]?.scheduled}</span>
                        )}
                        {Number.isFinite(schedule.bus_position?.lat) && (
                            <span className="vrs-meta-chip vrs-meta-pos" title={`${schedule.bus_position.lat.toFixed(6)}, ${schedule.bus_position.lng.toFixed(6)}`}>
                                <FaMapMarkerAlt /> {schedule.bus_position.lat.toFixed(5)}, {schedule.bus_position.lng.toFixed(5)}
                                {schedule.bus_position_time && ` · ${formatReportTime(schedule.bus_position_time)}`}
                            </span>
                        )}
                    </div>
                    {stops.length > 0 && (
                        <div className="vrs-progress">
                            <div className="vrs-progress-track">
                                <span className="vrs-progress-fill" style={{ width: `${progressPct}%` }} />
                            </div>
                            <div className="vrs-progress-meta">
                                <span>{doneCount} / {stops.length} {t.stops}</span>
                                {lastStop?.name && (
                                    <span className="vrs-progress-end"><FaFlagCheckered /> {lastStop.name}</span>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="vrs-header-sub">
                        <span className="vrs-source-note">{t.liveFromSchedule}</span>
                        {refreshedAt && !isProvisional && (
                            <span className="vrs-live">
                                <span className="vrs-live-dot" />
                                {t.updatedLabel} {moment(refreshedAt).format("HH:mm:ss")}
                            </span>
                        )}
                    </div>
                </div>

                <div className="vrs-list" ref={listRef}>
                    {shownPast.map((s, i) => (
                        <StopRow s={s} kind="past" isLastOfBlock={false} key={`p-${s.sequence}`} />
                    ))}

                    {currentStop && (
                        <StopRow s={currentStop} kind="current" isLastOfBlock={false} rowRef={currentRowRef} key={`c-${currentStop.sequence}`} />
                    )}

                    {futureStops.map((s, i) => (
                        <StopRow s={s} kind="future" isLastOfBlock={i === futureStops.length - 1} key={`f-${s.sequence}`} />
                    ))}
                </div>

                {stops.length === 0 && (
                    <div className="vr-no-all">
                        <div className="vr-no-text">{t.noRouteData}</div>
                    </div>
                )}
            </div>
        )
    }

    const scheduleReason =
        schedule && !schedule.schedule_available && schedule.reason
            ? (SCHEDULE_REASONS[language] || SCHEDULE_REASONS.en)[schedule.reason] || schedule.reason
            : null

    return (
        <div className="vr-container">
            {renderCompletedBanner()}
            {scheduleReason && schedule?.reason !== "trip_completed" && (
                <div className="vrs-fallback-note">ⓘ {scheduleReason}</div>
            )}
            {renderRunsSummary()}
            {renderForecastLink("vr-forecast-link")}

            <Card sx={{ p: "16px", mb: "20px", borderLeft: "4px solid #ff3b2f" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
                    {vehicle?.vehicleName}
                </Typography>
                <Typography sx={{ mt: 1 }}>
                    <strong>{translations[language]?.vehicleModelLabel}:</strong> {vehicle?.vehicleModel}
                </Typography>
                <Typography sx={{ mt: 1 }}>
                    <strong>{translations[language]?.registeringNumber}:</strong> {vehicle?.registeringNumber}
                </Typography>
                <Typography sx={{ mt: 1 }}>
                    <strong>{translations[language]?.txtRemainingPassengers}:</strong> {vehicle?.remaining ?? 0}
                </Typography>
                {Number.isFinite(parseFloat(vehicle?.lat)) && (
                    <Typography sx={{ mt: 1 }}>
                        <strong>📍</strong> {parseFloat(vehicle.lat).toFixed(6)}, {parseFloat(vehicle.lng).toFixed(6)}
                        {vehicle?.report_time && ` · 🕒 ${formatReportTime(vehicle.report_time)}`}
                    </Typography>
                )}
            </Card>

            {past?.length > 0 && (
                <div className="vr-section">
                    <div className="vr-section-title">{translations[language]?.pastStops}</div>

                    {past?.map((s, i) => (
                        <div className="vr-row" key={s?.id}>
                            <div className="vr-timeline">
                                <div className="vr-dot-small"></div>
                                {i < past.length - 1 && <div className="vr-line-grey"></div>}
                            </div>

                            <div className="vr-text">
                                <div className="vr-time-small">
                                    🕒 {s?.stop_reach_time && (
                                        <>
                                            {translations[language]?.reachedAt} <strong>{formatTime(s.stop_reach_time)}</strong> ·{' '}
                                        </>
                                    )}
                                    {translations[language]?.stoppedFor} <strong>{s?.total_minutes ?? 0}</strong> {translations[language]?.minutes}
                                </div>
                                <div className="vr-address-small">{s?.address}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {current && (
                <div className="vr-current-wrapper">
                    <div className="vr-current">
                        <div className="vr-timeline-current">
                            <div className="vr-dot-current"></div>
                        </div>

                        <div className="vr-current-text">
                            <div className="vr-current-label">{translations[language]?.currentBusLocation}</div>
                            <div className="vr-current-address">{current?.address}</div>
                            <div className="vr-current-time">{current?.time}</div>
                        </div>
                    </div>
                </div>
            )}

            {future?.length > 0 && (
                <div className="vr-section">
                    <div className="vr-section-title">{translations[language]?.nextStops}</div>

                    {future.map((s, i) => (
                        <div className="vr-row" key={s?.id}>
                            <div className="vr-timeline">
                                <div className="vr-dot-upcoming"></div>
                                {i < future.length - 1 && <div className="vr-line-red-dashed"></div>}
                            </div>

                            <div className="vr-text">
                                <div className="vr-address-large">{s?.address}</div>
                                <div className="vr-time-large">
                                    🕒 {translations[language]?.reachIn} <strong>{s?.eta_minutes || 0}</strong> {translations[language]?.minutes}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {past?.length === 0 && !current && future.length === 0 && (
                <div className="vr-no-all">
                    <div className="vr-no-text">{translations[language]?.noRouteData}</div>
                </div>
            )}

        </div>
    )
}

export default VehicleRoute
